import * as PIXI from 'pixi.js';
import Colors from '~/constants/Colors';
import { ObjectPool } from '~/Tools';

// Object pool for loss bar Graphics
const _lossBarPool = new ObjectPool(
  // create
  () => new PIXI.Graphics(),
  // reset
  (bar) => {
    bar.clear();
    bar.parent?.removeChild(bar);
    bar.x = 0;
    bar.y = 0;
    bar.scale.x = 1;
    bar.scale.y = 1;
    TweenMax.killTweensOf(bar);
    TweenMax.killTweensOf(bar.scale);
  },
  // initial size
  10
);

const UnitStatBar = function UnitStatBar(unit_state, { is_caverns = false } = {}) {
  PIXI.Container.call(this);

  let _last_hp = !isNaN(unit_state.hp) ? unit_state.hp : unit_state.max_hp;
  let _last_mp = !isNaN(unit_state.mp) ? unit_state.mp : unit_state.max_mp;
  let statBarWidth, statBarHeight;
  let _hp_bar_tween;
  let _mp_bar_tween;

  const hp_bar = new PIXI.Graphics();
  const mp_bar = is_caverns ? undefined : new PIXI.Graphics();
  let _hpLossBar, _mpLossBar;

  const drawStatBarBackGround = () => {
    if (is_caverns) {
      return;
    }

    this.background.clear();

    this.background.beginFill(
      unit_state.team === 'white' ? Colors.WHITE_TEAM_DARK : Colors.BLACK_TEAM_DARK
    );
    this.background.drawRect(1, 1, statBarWidth, statBarHeight);
    this.background.endFill();

    this.background.beginFill(Colors.MP_PURPLE_DARK);
    this.background.drawRect(1, 3 + statBarHeight, statBarWidth, statBarHeight);
    this.background.endFill();
  };

  this.updateStatBarSize = () => {
    if (!this.parent) {
      return;
    }
    statBarWidth = is_caverns ? 12 : Math.max(30, this.parent.getChildAt(0).width * 0.3);
    statBarHeight = is_caverns ? 1 : Math.max(4, this.parent.getChildAt(0).height * 0.015);
    drawStatBarBackGround(NaN);
  };

  this.init = () => {
    this.background = new PIXI.Graphics();
    this.addChild(this.background);
    this.updateStatBarSize();

    // Draw HP bar at full width once
    hp_bar.beginFill(
      unit_state.team === 'white'
        ? is_caverns
          ? Colors.HP_RED
          : Colors.WHITE_TEAM
        : Colors.BLACK_TEAM
    );
    hp_bar.drawRect(1, 1, statBarWidth, statBarHeight);
    hp_bar.endFill();
    this.addChild(hp_bar);

    // Draw MP bar at full width once (if not caverns)
    if (!is_caverns) {
      mp_bar.beginFill(Colors.MP_PURPLE);
      mp_bar.drawRect(1, 3 + statBarHeight, statBarWidth, statBarHeight);
      mp_bar.endFill();
      this.addChild(mp_bar);
    }
  };

  this.update = (new_unit_state) => {
    // Use scaling instead of clearing and redrawing for performance
    hp_bar.scale.x = new_unit_state.hp / new_unit_state.max_hp;

    if (!is_caverns) {
      mp_bar.scale.x = new_unit_state.mp / new_unit_state.max_mp;
    }

    if (new_unit_state.hp < _last_hp) {
      // hp have been spent or lost
      const hpLeft = (statBarWidth * Math.max(new_unit_state.hp, 0.1)) / new_unit_state.max_hp;
      const lossWidth = statBarWidth * ((_last_hp - new_unit_state.hp) / new_unit_state.max_hp);

      if (_hpLossBar) {
        _lossBarPool.put(_hpLossBar);
      }
      _hpLossBar = _lossBarPool.get();
      _hpLossBar.beginFill(0xffffff);
      _hpLossBar.drawRect(0, 1, lossWidth, statBarHeight);
      _hpLossBar.endFill();
      _hpLossBar.x = hpLeft;
      this.addChild(_hpLossBar);

      _hp_bar_tween?.kill();
      _hp_bar_tween = TweenMax.to(_hpLossBar.scale, 0.8, {
        x: 0,
        onComplete: (bar) => {
          _lossBarPool.put(bar);
          _hp_bar_tween = null;
        },
        onCompleteParams: [_hpLossBar],
      });
    }

    if (new_unit_state.mp < _last_mp && !is_caverns) {
      // mp have been spent or lost
      const mpLeft = (statBarWidth * Math.max(new_unit_state.mp, 0.1)) / new_unit_state.max_mp;
      const lossWidth = statBarWidth * ((_last_mp - new_unit_state.mp) / new_unit_state.max_mp);

      if (_mpLossBar) {
        _lossBarPool.put(_mpLossBar);
      }
      _mpLossBar = _lossBarPool.get();
      _mpLossBar.beginFill(0xffffff);
      _mpLossBar.drawRect(0, 3 + statBarHeight, lossWidth, statBarHeight);
      _mpLossBar.endFill();
      _mpLossBar.x = mpLeft;
      this.addChild(_mpLossBar);

      _mp_bar_tween?.kill();
      _mp_bar_tween = TweenMax.to(_mpLossBar.scale, 0.8, {
        x: 0,
        onComplete: (bar) => {
          _lossBarPool.put(bar);
          _mp_bar_tween = null;
        },
        onCompleteParams: [_mpLossBar],
      });
    }

    _last_hp = new_unit_state.hp;
    _last_mp = new_unit_state.mp;
  };

  this.dispose = () => {
    _hp_bar_tween?.kill();
    _mp_bar_tween?.kill();
    _hp_bar_tween = null;
    _mp_bar_tween = null;

    if (_hpLossBar) {
      _lossBarPool.put(_hpLossBar);
      _hpLossBar = null;
    }
    if (_mpLossBar) {
      _lossBarPool.put(_mpLossBar);
      _mpLossBar = null;
    }

    this.unit = null;
    DT_CANVAS_GLOBALS.poolSafeDestroy(this, { children: true });
  };
};
UnitStatBar.prototype = Object.create(PIXI.Container.prototype);
UnitStatBar.prototype.constructor = UnitStatBar;
export default UnitStatBar;
